// Libraries
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { mergeRight } from 'ramda';
import { useSnackbar } from 'notistack';

// MUI
import { Container, Badge, styled, Tooltip, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { Add, HourglassEmptyTwoTone, NotificationsNone, PictureAsPdf } from '@mui/icons-material';
import { GridRenderCellParams } from '@mui/x-data-grid-pro';

// Core
import { dayFormat } from '../../../../../../core/constants';
import {
    ParameterValues,
    paramRepository,
    ResourceTextApplication,
} from '../../../../../../core/resources';
import auth from '../../../../../../core/authorization';

// Common
import {
    ADataGrid,
    ADataGridActions,
    AGridColumns,
    gridColumns,
} from '../../../../../../common/dataGrid';
import {
    ADefaultButton,
    ADefaultIconButton,
    ADeleteIconButton,
    AEditIconButton,
    ASearchIconButton,
} from '../../../../../../common/buttons';
import { TextsWithTooltip, TextWithTooltip } from '../../../../../../common/typography';
import AAttachmentIconButton from '../../../../../../common/buttons/AAttachmentIconButton';
import AWarningIconButton from '../../../../../../common/buttons/AWarningIconButton';
import ADoneIconButton from '../../../../../../common/buttons/ADoneIconButton';
import APriorityHighIconButton from '../../../../../../common/buttons/APriorityHighIconButton';

// Interfaces
import { AnnouncementTypeEnum } from '../../../../../../interfaces/enums';
import {
    IAnimalAnnouncement,
    IAnimalAnnouncementAnimal,
    IAnimalAnnouncementBatch,
    IAnimalAnnouncementWithFarm,
    IAnnouncementFarm,
} from '../../../../../../interfaces/IAnnouncement';
import { GUIDType } from '../../../../../../interfaces/types';

// Store
import { useAppSelector } from '../../../../../../store/hooks';

// API
import animalAnnouncementApi from '../../../../../../api/animalAnnouncementApi';
import chainInformationApi from '../../../../../../api/chainInformationApi';
import printingApi from '../../../../../../api/printingApi';

// Feature - Animal Announcement
import ConfirmDialog from '../../../../../shared/components/confirm/ConfirmDialog';
import { Strings } from '../../../../../shared/constants/strings';
import {
    calculateAmountOfAnimals,
    calculateOrderedAnimalCount,
    collectWeekComparator,
    getAnnouncementAnimals,
    getUniqueCollectWeeks,
    handleAnimalAnnouncementDeletionRestrictions,
    isAnimalAnnouncementUpdateRestricted,
    hasAnnouncementResponseErrors,
} from '../../../../helpers/data';
import { AnimalAnnouncementsListViewStrings } from '../../strings/AnimalAnnouncementsListViewStrings';
import { isAnimalsAndSalesType } from '../../../../../shared/helpers/data';

// Feature - Chain Information
import { IChainInformationData } from '../../../../../chain-information/IChainInformationDialog';
import ChainInformationDialog from '../../../../../chain-information/components/ChainInformationDialog';

// Feature - Persons
import { fetchPersonRelations } from '../../../../../../persons/fetchPersonRelations';
import producerReturnApi from '../../../../../../api/producerReturnApi';
import { IProducerReturnSummary } from '../../../../../../interfaces/IProducerReturn';
import { AnnouncementStatus } from '../../../../../shared/types/announcementStatus';
import FormValidator from '../../../../../../core/FormValidator';
import { IChainInformationNotify } from '../../interfaces/IChainInformationNotify';
import ConfirmBovinePurchaseAnnouncement from '../../../bovine-purchase-announcement/confirm/ConfirmBovinePurchaseAnnouncement';
import notificationApi from '../../../../../../api/notificationApi';
import { IApiResultMessage } from '../../../../../../interfaces/IGeneral';

export interface IAnimalAnnouncementListViewGridProps {
    data: IAnimalAnnouncement[];
    loading: boolean;
    handleAnimalAnnouncementReload: (announcementId: GUIDType) => void;
    handleAnimalAnnouncementDelete: (announcementNumber: number) => void;
    handleAnimalAnnouncementDeleteById: (announcementId: GUIDType) => void;
    handleAnimalAnnouncementModification: (item: IAnimalAnnouncement) => void;
    canCreateAnimalAnnouncement: boolean | null;
    reloadChainInfos: boolean;
    setReloadChainInfos: (val: boolean) => void;
    displayAll?: boolean;
}

const StyledBadge = styled(Badge)(({}) => ({
    marginLeft: '1em',
}));

// announcement list in announcement search view
export default function AnimalAnnouncementListViewGrid(
    props: IAnimalAnnouncementListViewGridProps,
) {
    const { t } = useTranslation<ResourceTextApplication[]>([
        'AnelmaBovine',
        'AnelmaGeneral',
        'AnelmaTiltu',
        'AnelmaCompany'
    ]);
    const { enqueueSnackbar } = useSnackbar();

    const userData = useAppSelector((state) => state.userData.data);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [confirmDialogVisible, setConfirmDialogVisible] = useState<boolean>(false);
    const [selectedAnnouncement, setSelectedAnnouncement] = useState<string>('');
    const [collectWeekDayParams, setCollectWeekDayParams] = useState<ParameterValues>([]);
    const [bovineSalesTypeParams, setBovineSalesTypeParams] = useState<ParameterValues>([]);
    const [selectedRow, setSelectedRow] = useState<IAnimalAnnouncement>();
    const [chainInformations, setChainInformations] = useState<IChainInformationData[]>([]);
    const [chainInformationDialogVisible, setChainInformationDialogVisible] =
        useState<boolean>(false);
    const [announcementId, setAnnouncementId] = useState<GUIDType>('');
    const [announcementNumber, setAnnouncementNumber] = useState<string>('');
    const [animalsForChainInformation, setAnimalsForChainInformation] = useState<
        IAnimalAnnouncementAnimal[]
        >([]);
    const [farmForChainInformation, setFarmForChainInformation] = useState<IAnnouncementFarm | null>(null);
    const [isLoadingChainInfos, setIsLoadingChainInfos] = useState<boolean>(false);
    const [announcementIds, setAnnouncementIds] = useState<string[]>(props.data.map((i) => i.Id));
    const [snellmanUser, setSnellmanUser] = useState<boolean>(false);

    const [producerReturns, setProducerReturns] = useState<IProducerReturnSummary[]>([]);

    const checkChainInfoFound = (announcementId: GUIDType) => {
        let chainInfoFound = chainInformations.find((c) => c.AnnouncementId === announcementId);
        if (chainInfoFound) {
            return false;
        } else {
            return true;
        }
    };

    const checkChainInfoConfirmed = (announcementId: GUIDType) => {
        let chainInfoToCheck = chainInformations.find((c) => c.AnnouncementId === announcementId);
        if (chainInfoToCheck?.FormConfirmation) {
            return true;
        } else {
            return false;
        }
    };

    const checkChainInfoNotify = (announcementId: GUIDType) => {
        let chainInfoToCheck = chainInformations.find((c) => c.AnnouncementId === announcementId);
        if (chainInfoToCheck) {
            if (
                chainInfoToCheck.MedicatedFeed === true ||
                chainInfoToCheck.HealthInformation === true ||
                chainInfoToCheck.MedicalAccounting === true ||
                chainInfoToCheck.AuthorityRegulations === true ||
                chainInfoToCheck.ForeignObject === true ||
                chainInfoToCheck.GmFree === true ||
                chainInfoToCheck.AdditionalInfo.length > 0 ||
                chainInfoToCheck.MedicatedAnimals.length > 0
            ) {
                return true;
            } else {
                return false;
            }
        }
    };

    const getChainInformationNotifyBadge = (announcementId: string) => {
        const marked = getChainInformationNotifyData(announcementId);

        if (marked.length == 0)
            return <></>;

        return (
            <Tooltip title={marked.join(', ')} followCursor>
                <span>
                    <StyledBadge badgeContent={marked[0]} color='warning'></StyledBadge>
                    {marked.length > 1 && (
                        <Add color='warning' sx={{ fontWeight: 'bold' }} />
                    )}
                </span>
            </Tooltip>
        );
    };

    const calculateChainInformationIssues = (chainInformation: IChainInformationData): number[] => {
        const issueFields: { condition: boolean; number: number }[] = [
            { condition: chainInformation.MedicatedFeed === true, number: 1 },
            { condition: chainInformation.MedicatedAnimals.length > 0, number: 2 },
            { condition: chainInformation.HealthInformation === true, number: 3 },
            { condition: chainInformation.AuthorityRegulations === true, number: 4 },
            { condition: chainInformation.ForeignObject === true, number: 5 },
            { condition: chainInformation.GmFree === true, number: 7 },
            { condition: chainInformation.AdditionalInfo.length > 0, number: 8 },
        ];

        return issueFields.filter((field) => field.condition).map((field) => field.number);
    };

    const getChainInformationNotifyData = (announcementId: string): number[] => {
        const chainInformation = chainInformations.find((c) => c.AnnouncementId === announcementId);

        if (!chainInformation) return [];

        return calculateChainInformationIssues(chainInformation);
    };

    const openChainInformationDialog = (data: any) => {
        setAnnouncementId(data.Id);
        setAnnouncementNumber(data.Number);
        setFarmForChainInformation(data.Farm ?? null);
        setAnimalsForChainInformation(getAnnouncementAnimals(data));
        setChainInformationDialogVisible(true);
    };

    const toggleChainInformationReadOnly = (announcementId: GUIDType): boolean => {
        if (announcementId) {
            const selectedAnnouncement = props.data.find((_) => _.Id === announcementId);
            if (selectedAnnouncement !== undefined)
                return isAnimalAnnouncementUpdateRestricted(selectedAnnouncement);
        }
        return false;
    };

    const checkChainInformationAuth = (announcementId: GUIDType) => {
        if (
            auth.canUpdateChainInformationAdmin === true &&
            checkChainInfoFound(announcementId) === false &&
            auth.canViewChainInformation === false
        ) {
            return false;
        } else if (
            auth.canUpdateChainInformationAdmin &&
            checkChainInfoFound(announcementId) === true
        ) {
            return false;
        } else if (auth.canViewChainInformation) {
            return false;
        } else {
            return true;
        }
    };

    const columns: AGridColumns = [
        {
            field: AnimalAnnouncementsListViewStrings.Status,
            headerName: t('AnelmaBovine:1035'),
            renderCell: (params) => <TextWithTooltip text={String(params.value)} />,
            valueGetter: (params, row) =>
                row.Status !== undefined && row.Status !== null ? row.Status : '',
            width: 80,
        },
        {
            field: AnimalAnnouncementsListViewStrings.AnnouncementNumberField,
            headerName: t('AnelmaBovine:1137'),
            renderCell: (params) => {
                const value = params.value as number;
                const announcement = params.row as IAnimalAnnouncement;

                var i = props.data.findIndex(a => a.Number === value);
                var j = props.data.findIndex(a => a.PreliminaryNumber === announcement.PreliminaryNumber);
                var hasUnknownBatchNumber = false;

                if (i !== -1) {
                    hasUnknownBatchNumber = props.data[i].Batches.findIndex(b => b.BatchNumber === null) !== -1;
                }

                if (j !== -1 && isPreliminaryAnnouncement(props.data[j])) {
                    if (!props.data[j].PreliminaryNumber)
                        return '';

                    return 'AL' + props.data[j].PreliminaryNumber;
                }

                return (value === 0 || hasUnknownBatchNumber)
                    ? (
                        <ADefaultIconButton
                            icon={
                                <>
                                    <HourglassEmptyTwoTone />
                                    {params.value && Number(params.value) > 0 ? params.value : ''}
                                </>}
                            tooltip={t('AnelmaBovine:1159')?.toString() ?? ''}
                            onClick={() => {
                                const announcementId = params.row.Id as GUIDType;
                                animalAnnouncementApi.animalAnnouncementState(announcementId).then((res) => {
                                    if (
                                        res?.Entity.Status === AnnouncementStatus.Completed ||
                                        res?.Entity.Status === AnnouncementStatus.Failed
                                    ) {
                                        props.handleAnimalAnnouncementReload(announcementId);
                                    }
                                });
                            }}
                            disabled={false}
                        />
                    ) : (
                        value
                    );
            },
            width: 80,
        },
        {
            field: AnimalAnnouncementsListViewStrings.TypeField,
            headerName: t('AnelmaBovine:1029'),
            renderCell: (params) => {
                let val = '';

                if (params.value)
                    val = params.value as string;

                // AL.TE
                if (val.includes(t('AnelmaBovine:1222')))
                    return (
                        <Tooltip title={val.replace(t('AnelmaBovine:1222'), t('AnelmaBovine:1234'))}>
                            <Typography noWrap variant='body2'>
                                {val}
                            </Typography>
                        </Tooltip>
                    );

                // AL.VÄ
                if (val.includes(t('AnelmaBovine:1223')))
                    return (
                        <Tooltip title={val.replace(t('AnelmaBovine:1223'), t('AnelmaBovine:1235'))}>
                            <Typography noWrap variant='body2'>
                                {val}
                            </Typography>
                        </Tooltip>
                    );

                return <TextWithTooltip text={val} />;
            },
            valueGetter: (params, row) => resolveAnnouncementTypeAsText(row.Type),
            width: 120,
        },
        {
            ...gridColumns.dateTime(
                AnimalAnnouncementsListViewStrings.CreatedField,
                t('AnelmaBovine:1138'),
                dayFormat,
                100,
            ),
        },
        {
            field: AnimalAnnouncementsListViewStrings.BatchesFields,
            headerName: t('AnelmaBovine:1139'),
            renderCell: (params) => {
                return <TextsWithTooltip texts={params.value as string[]} />;
            },
            valueGetter: (params, row) =>
                getUniqueCollectWeeks(row.Batches as IAnimalAnnouncementBatch[]),
            sortComparator: (x1, x2) =>
                collectWeekComparator(String((x1 as any[])[0]), String((x2 as any[])[0])),
            width: 120,
        },
        {
            field: AnimalAnnouncementsListViewStrings.ChainInformationField,
            headerName: t('AnelmaBovine:1067'),
            renderCell: (params) => {
                switch ((params.row as IAnimalAnnouncement).Type) {
                    case AnnouncementTypeEnum.Slaughter:
                        return (
                            <Grid style={{ display: 'flex', alignItems: 'center' }}>
                                <AAttachmentIconButton
                                    key={`${params.id}-attachment`}
                                    onClick={() => {
                                        openChainInformationDialog(params.row);
                                    }}
                                    disabled={
                                        params.row.Number === 0 
                                        || isPreliminaryAnnouncement(params.row as IAnimalAnnouncement)
                                        || checkChainInformationAuth(params.row.Id)
                                    }
                                    tooltip={t('AnelmaBovine:1143')}
                                />
                                {checkChainInfoFound(params.row.Id) && (
                                    <AWarningIconButton
                                        onClick={() => {}}
                                        tooltip={t('AnelmaBovine:1144')}
                                    />
                                )}
                                {checkChainInfoConfirmed(params.row.Id) && (
                                    <ADoneIconButton
                                        onClick={() => {}}
                                        tooltip={t('AnelmaBovine:1145')}
                                    />
                                )}
                                {checkChainInfoNotify(params.row.Id) && (
                                    <Grid>
                                        <APriorityHighIconButton
                                            onClick={() => {}}
                                            tooltip={t('AnelmaBovine:1146')}
                                        />
                                        {getChainInformationNotifyBadge(params.row.Id)}
                                    </Grid>
                                )}
                            </Grid>
                        );
                    case AnnouncementTypeEnum.SlaughterWithChainInformation:
                        return (
                            <Grid style={{ display: 'flex', alignItems: 'center' }}>
                                <AAttachmentIconButton
                                    key={`${params.id}-attachment`}
                                    onClick={() => {
                                        openChainInformationDialog(params.row);
                                    }}
                                    disabled={checkChainInformationAuth(params.row.Id)}
                                    tooltip={t('AnelmaBovine:1143')}
                                />
                                {checkChainInfoFound(params.row.Id) && (
                                    <AWarningIconButton
                                        onClick={() => {}}
                                        tooltip={t('AnelmaBovine:1144')}
                                    />
                                )}
                                {checkChainInfoConfirmed(params.row.Id) && (
                                    <ADoneIconButton
                                        onClick={() => {}}
                                        tooltip={t('AnelmaBovine:1145')}
                                    />
                                )}
                                {checkChainInfoNotify(params.row.Id) && (
                                    <Grid>
                                        <APriorityHighIconButton
                                            onClick={() => {}}
                                            tooltip={t('AnelmaBovine:1146')}
                                        />
                                        {getChainInformationNotifyBadge(params.row.Id)}
                                    </Grid>
                                )}
                            </Grid>
                        );
                    case AnnouncementTypeEnum.ManualBovineSlaughter:
                        return (
                            <Grid style={{ display: 'flex', alignItems: 'center' }}>
                                <AAttachmentIconButton
                                    key={`${params.id}-attachment`}
                                    onClick={() => {
                                        openChainInformationDialog(params.row);
                                    }}
                                    disabled={checkChainInformationAuth(params.row.Id)}
                                    tooltip={t('AnelmaBovine:1143')}
                                />
                                {checkChainInfoFound(params.row.Id) && (
                                    <AWarningIconButton
                                        onClick={() => {}}
                                        tooltip={t('AnelmaBovine:1144')}
                                    />
                                )}
                                {checkChainInfoConfirmed(params.row.Id) && (
                                    <ADoneIconButton
                                        onClick={() => {}}
                                        tooltip={t('AnelmaBovine:1145')}
                                    />
                                )}
                                {checkChainInfoNotify(params.row.Id) && (
                                    <Grid>
                                        <APriorityHighIconButton
                                            onClick={() => {}}
                                            tooltip={t('AnelmaBovine:1146')}
                                        />
                                        {getChainInformationNotifyBadge(params.row.Id)}
                                    </Grid>
                                )}
                            </Grid>
                        );
                    default:
                        return <></>;
                }
            },
            width: 150,
            sortable: false,
        },
        {
            field: AnimalAnnouncementsListViewStrings.AmountField,
            headerName: t('AnelmaBovine:1037'),
            renderCell: (params) => {
                return <TextWithTooltip text={String(params.value)} />;
            },
            valueGetter: (params, row) => getAnimalCount(row.Batches, row.Type),
            width: 110,
        },
        {
            field: AnimalAnnouncementsListViewStrings.SalesTypeField,
            headerName: t('AnelmaBovine:1019'),
            renderCell: (params) => {
                return <TextWithTooltip text={String(params.value)} />;
            },
            valueGetter: (params, row) => {
                if ( (row.Type as AnnouncementTypeEnum) !== AnnouncementTypeEnum.BovinePurchase
                    && (row.Type as AnnouncementTypeEnum) !== AnnouncementTypeEnum.PreliminaryBovinePurchaseAnnouncement ) {
                    return (
                        row.Batches.slice(0, 1).map((batch: IAnimalAnnouncementBatch) => {
                            return getSalesType(
                                isAnimalsAndSalesType(batch) ? batch.Animals[0].SalesType : 0,
                                bovineSalesTypeParams,
                            );
                        })[0] || ''
                    );
                }

                return (
                    row.Batches.slice(0, 1).map((batch: IAnimalAnnouncementBatch) => {
                        return getSalesType(
                            batch.SalesType !== null && batch.SalesType !== undefined
                                ? batch.SalesType
                                : 0,
                            bovineSalesTypeParams,
                        );
                    })[0] || ''
                );
            },
            width: 100,
        },
        gridColumns.withTooltip({
            field: AnimalAnnouncementsListViewStrings.HoldingSiteField,
            headerName: t('AnelmaBovine:1140'),
            width: 125,
        }),
        gridColumns.withTooltip({
            field: AnimalAnnouncementsListViewStrings.InfoField,
            headerName: t('AnelmaBovine:1039'),
            width: 160,
        }),
        gridColumns.withTooltip({
            field: AnimalAnnouncementsListViewStrings.AttachmentsField,
            headerName: t('AnelmaBovine:1038'),
            width: 70,
        }),
    ];

    const getColumns = () => {
        let res = [...columns];

        // admin
        if (auth.canViewAdminContextSelector) {
            res.push({
                field: 'SendNotificationButton',
                headerName: t('AnelmaBovine:1227'),
                renderCell: (params) => {
                    return isPreliminaryAnnouncement(params.row as IAnimalAnnouncement)
                        && (
                            <Grid container>
                                <ADefaultButton
                                    children={t('AnelmaBovine:1228')}
                                    onClick={() => sendPreliminaryAnnouncementNotification(
                                        params.row as IAnimalAnnouncement
                                    )}
                                    size={'small'}
                                    style={{ height: '25px', width: '110px' }}
                                />
                            </Grid>
                        );
                },
                width: 150,
                filterable: false,
            });
        }

        if (props.displayAll
            && props.data.find(d => (d as IAnimalAnnouncementWithFarm)?.Farm)) {
            res.splice(2, 0, {
                field: 'Farm',
                headerName: t('AnelmaCompany:1001'),
                renderCell: (params) => {
                    return <TextWithTooltip text={String(params.value)} />;
                },
                valueGetter: (params, row) =>
                    (row as IAnimalAnnouncementWithFarm).Farm?.ProducerNumber
                    + ' ' + (row as IAnimalAnnouncementWithFarm).Farm?.Name,
                width: 120,
            });
        }

        return res;
    }

    useEffect(() => {
        paramRepository.load(['AnelmaAnimalAnnouncement', 'AnelmaBovine']).then(() => {
            setCollectWeekDayParams(
                paramRepository.resource('AnelmaAnimalAnnouncement', 'CollectWeekDay'),
            );
            setBovineSalesTypeParams(paramRepository.resource('AnelmaBovine', 'SalesType'));
        });
    }, []);

    useEffect(() => {
        if (userData) {
            fetchPersonRelations(userData.Logins[0].Login).then((response) => {
                setSnellmanUser(response[0]);
            });
        }
    }, [userData]);

    useEffect(() => {
        setAnnouncementIds(props.data.map((i) => i.Id));
    }, [props.data]);

    useEffect(() => {
        reloadChainInfo();
    }, [announcementIds]);

    useEffect(() => {
        if (props.reloadChainInfos) {
            reloadChainInfo();
            props.setReloadChainInfos(false);
        }
    }, [props.reloadChainInfos]);

    const reloadChainInfo = () => {
        let ids = props.data.map((i) => i.Id);

        if (ids.length > 0 && isLoadingChainInfos === false) {
            setIsLoadingChainInfos(true);
            chainInformationApi.loadMultipleChainInformations(ids).then((response) => {
                if (response) {
                    let chainInfos: IChainInformationData[] = [];
                    for (let i = 0; i < response.Items.length; i++) {
                        let chainInfo: IChainInformationData = {
                            AnnouncementId: response.Items[i].AnnouncementId,
                            MedicatedFeed: response.Items[i].MedicatedFeed,
                            HealthInformation: response.Items[i].HealthInformation,
                            AuthorityRegulations: response.Items[i].AuthorityRegulations,
                            ForeignObject: response.Items[i].ForeignObject,
                            GmFree: response.Items[i].GmFree,
                            MedicalAccounting: response.Items[i].MedicalAccounting,
                            WaitingTimeOver: response.Items[i].WaitingTimeOver,
                            NasevaInfo: response.Items[i].NasevaInfo,
                            SlaughterHouseInfo: response.Items[i].SlaughterHouseInfo,
                            MedicationInfo: response.Items[i].MedicationInfo,
                            Anomalies: response.Items[i].Anomalies,
                            Regulations: response.Items[i].Regulations,
                            SamplingOrder: response.Items[i].SamplingOrder,
                            VetName: response.Items[i].VetName,
                            AdditionalInfo: response.Items[i].AdditionalInfo,
                            ForeignObjectInfo: response.Items[i].ForeignObjectInfo,
                            AnimalSymptoms: response.Items[i].Symptoms,
                            OtherSymptom: response.Items[i].OtherSymptom,
                            AnimalDiseases: response.Items[i].Diseases,
                            OtherDisease: response.Items[i].OtherDisease,
                            MedicatedAnimals: response.Items[i].Medications,
                            FormConfirmation: response.Items[i].FormConfirmation,
                            Signature: response.Items[i].Signature,
                            Signatory: response.Items[i].Signatory,
                            SignatureDate: response.Items[i].SignatureDate,
                        };
                        chainInfos.push(chainInfo);
                    }
                    setChainInformations(chainInfos);
                    setIsLoadingChainInfos(false);
                }
            });
        }
    };

    useEffect(() => {
        setIsLoading(props.loading);
    }, [props.loading]);

    useEffect(() => {
        if (props.canCreateAnimalAnnouncement !== null && !props.canCreateAnimalAnnouncement) {
            enqueueSnackbar(t('AnelmaBovine:1092'), {
                variant: 'warning',
            });
        }
    }, [props.canCreateAnimalAnnouncement]);

    const slaughterAnnouncements = () => {
        let allowed = [
            AnnouncementTypeEnum.Slaughter as number,
            AnnouncementTypeEnum.SlaughterWithChainInformation as number,
            AnnouncementTypeEnum.ManualBovineSlaughter as number,
        ];

        return props.data.filter((a) => allowed.includes(a.Type));
    };

    const sendPreliminaryAnnouncementNotification = (announcement: IAnimalAnnouncement) => {
        notificationApi
            .sendPreliminaryAnnouncementNotification(announcement.Id)
            .then((response) => {
                if (!response)
                    enqueueSnackbar(t('AnelmaGeneral:1019'), {
                        variant: 'error',
                    });

                enqueueSnackbar(t('AnelmaBovine:1236'), {
                    variant: 'success',
                });
            })
            .catch((error: IApiResultMessage[]) => {
                if (error) {
                    error.forEach((e) =>
                        enqueueSnackbar(e?.Explanation ?? t('AnelmaGeneral:1019'), {
                            variant: 'error',
                        })
                    );
                }
            });
    }

    const initialProducerReturnDataFetch = async () => {
        producerReturnApi
            .getProducerReturnListByAnnouncements(slaughterAnnouncements().map((a) => a.Id))
            .then((response) => {
                if (!response) {
                    enqueueSnackbar(`${t('AnelmaGeneral:1019')}`, {
                        variant: 'error',
                    });
                    return;
                }

                setProducerReturns(response.Items);
            });
    };

    const handleConfirmDeleteDialogData = (id: string) => {
        setConfirmDialogVisible(true);
        setSelectedAnnouncement(id);
    };

    const deleteAnimalAnnouncement = () => {
        setIsLoading(true);
        const animalAnnouncement = props.data.find((a) => a.Id === selectedAnnouncement);
        if (animalAnnouncement !== undefined) {
            if (animalAnnouncement.Number && !isPreliminaryAnnouncement(animalAnnouncement)) {
                animalAnnouncementApi
                    .deleteAnimalAnnouncement(Number(animalAnnouncement.Number))
                    .then((response) => {
                        if (!response) {
                            enqueueSnackbar(`${t('AnelmaGeneral:1019')}`, {
                                variant: 'error',
                            });
                            setIsLoading(false);
                            return;
                        }
                        if (hasAnnouncementResponseErrors(response)) {
                            const errorCodes = Array.from(
                                new Set(response.Summary.Errors.map((_) => _.Code)),
                            );
                            for (const errorCode of errorCodes) {
                                enqueueSnackbar(t(`AnelmaTiltu:${errorCode}`), {
                                    variant: 'error',
                                });
                            }
                            setIsLoading(false);
                            return;
                        }
                        setSelectedAnnouncement('');
                        props.handleAnimalAnnouncementDelete(animalAnnouncement.Number);
                        setIsLoading(false);
                        const type = animalAnnouncement.Type;
                        if (
                            type !== AnnouncementTypeEnum.ManualBovineIntermediation &&
                            type !== AnnouncementTypeEnum.ManualBovineSlaughter
                        )
                            chainInformationApi.deleteChainInformation(selectedAnnouncement).then();
                    });
            } else {
                animalAnnouncementApi
                    .deleteAnimalAnnouncementById(animalAnnouncement.Id)
                    .then((response) => {
                        setSelectedAnnouncement('');
                        props.handleAnimalAnnouncementDeleteById(animalAnnouncement.Id);
                        setIsLoading(false);
                    })
                    .catch((err) => {
                        console.log('ERR', err);
                    });
            }
        }
    };

    const resolveAnnouncementTypeAsText = (type: AnnouncementTypeEnum): string => {
        switch (type) {
            case AnnouncementTypeEnum.Slaughter:
            case AnnouncementTypeEnum.SlaughterWithChainInformation:
            case AnnouncementTypeEnum.ManualBovineSlaughter:
                return t('AnelmaBovine:1070');
            case AnnouncementTypeEnum.Intermediation:
            case AnnouncementTypeEnum.ManualBovineIntermediation:
                return t('AnelmaBovine:1071');
            case AnnouncementTypeEnum.BovinePurchase:
            case AnnouncementTypeEnum.PreliminaryBovinePurchaseAnnouncement:
                return t('AnelmaBovine:1072');
            case AnnouncementTypeEnum.PreliminaryBovineSlaughterAnnouncement:
                return t('AnelmaBovine:1222');
            case AnnouncementTypeEnum.PreliminaryBovineIntermediationAnnouncement:
                return t('AnelmaBovine:1223');
        }
    };

    const resolveEditTooltipText = (type: AnnouncementTypeEnum): string => {
        switch (type) {
            case AnnouncementTypeEnum.Slaughter:
                return t('AnelmaBovine:1044');
            default:
                return t('AnelmaGeneral:1075');
        }
    };

    const getSalesType = (salesType: number, bovineSalesTypeParams: ParameterValues) => {
        return salesType > 0
            ? bovineSalesTypeParams.find((_) => _.code === String(salesType))?.text
            : '';
    };

    const getAnimalCount = (
        batches: IAnimalAnnouncementBatch[],
        announcementType: AnnouncementTypeEnum,
    ): number => {
        switch (announcementType) {
            case AnnouncementTypeEnum.Slaughter:
            case AnnouncementTypeEnum.SlaughterWithChainInformation:
            case AnnouncementTypeEnum.ManualBovineSlaughter:
            case AnnouncementTypeEnum.PreliminaryBovineSlaughterAnnouncement:
                return calculateAmountOfAnimals(batches);
            case AnnouncementTypeEnum.Intermediation:
            case AnnouncementTypeEnum.ManualBovineIntermediation:
            case AnnouncementTypeEnum.PreliminaryBovineIntermediationAnnouncement:
                return calculateAmountOfAnimals(batches);
            case AnnouncementTypeEnum.BovinePurchase:
            case AnnouncementTypeEnum.PreliminaryBovinePurchaseAnnouncement:
                return calculateOrderedAnimalCount(batches);
        }
    };

    const isPreliminaryAnnouncement = (announcement: IAnimalAnnouncement) => {
        return announcement.Type == AnnouncementTypeEnum.PreliminaryBovineIntermediationAnnouncement
            || announcement.Type == AnnouncementTypeEnum.PreliminaryBovineSlaughterAnnouncement
            || announcement.Type == AnnouncementTypeEnum.PreliminaryBovinePurchaseAnnouncement;
    }

    const handleEditBovineAnnouncement = (item: IAnimalAnnouncement) => {
        setSelectedRow(item);
        props.handleAnimalAnnouncementModification(item as IAnimalAnnouncement);
        reloadChainInfo();
    };

    const statusAllowsEdit = (params: GridRenderCellParams) => {
        const item = params.row as unknown as IAnimalAnnouncement;
        if (item.Status === 'LO') return false;
        return true;
    };

    // Row had GridRowData parameter. Please, check the correct type and provide it as a parameter
    const handleAnimalAnnouncementUpdateBtnDisabled = (row: any) => {
        if (props.canCreateAnimalAnnouncement !== null && !props.canCreateAnimalAnnouncement)
            return true;
        return isAnimalAnnouncementUpdateRestricted(row as IAnimalAnnouncement);
    };

    const announcementSearchBtnDisabled = () => {
        if (auth.canViewBovineSlaughterAnnouncementDialog) {
            return false;
        } else {
            return true;
        }
    };

    const toggleAnnouncementReadOnlyMode = (params: GridRenderCellParams): JSX.Element => {
        if (isAnimalAnnouncementUpdateRestricted(params.row as IAnimalAnnouncement))
            return (
                <ASearchIconButton
                    key={`${params.id}-search`}
                    onClick={() => {
                        let item = params.row as unknown as IAnimalAnnouncement;
                        handleEditBovineAnnouncement(item);
                    }}
                    tooltip={t('AnelmaBovine:1163')}
                    disabled={
                        (params?.row?.Number === 0
                            && (params.row as IAnimalAnnouncement).Type !== AnnouncementTypeEnum.PreliminaryBovinePurchaseAnnouncement)
                        || announcementSearchBtnDisabled()
                    }
                />
            );

        return (
            <AEditIconButton
                key={`${params.id}-edit`}
                onClick={() => {
                    let item = params.row as unknown as IAnimalAnnouncement;
                    handleEditBovineAnnouncement(item);
                }}
                tooltip={resolveEditTooltipText(params.row.Type)}
                disabled={handleAnimalAnnouncementUpdateBtnDisabled(params.row)}
            />
        );
    };

    const dataGridActions: ADataGridActions = [
        (params) => {
            {
                return statusAllowsEdit(params) ? (
                    auth.canDeleteAnelmaBovineBrokerPurchase && (
                        <ADeleteIconButton
                            key={`${params.id}-delete`}
                            onClick={() => {
                                handleConfirmDeleteDialogData(String(params.id));
                            }}
                            tooltip={t('AnelmaGeneral:1012')}
                            disabled={handleAnimalAnnouncementDeletionRestrictions(
                                params.row as IAnimalAnnouncement,
                                snellmanUser,
                                producerReturns.findIndex(
                                    (r) =>
                                        r.AnnouncementId === (params.row as IAnimalAnnouncement).Id,
                                ) != -1,
                            )}
                        />
                    )
                ) : (
                    <></>
                );
            }
        },
        (params) => {
            {
                return (
                    auth.canUpdateAnelmaBovineBrokerPurchase &&
                    toggleAnnouncementReadOnlyMode(params)
                );
            }
        },
        (params) => {
            let canPrint =
                auth.canViewAnelmaBovineBrokerPurchase &&
                (params.row as IAnimalAnnouncement).Number !== 0 &&
                !isPreliminaryAnnouncement(params.row as IAnimalAnnouncement) &&
                !announcementSearchBtnDisabled();

            return (
                <ADefaultIconButton
                    icon={<PictureAsPdf />}
                    onClick={() => {
                        printingApi.printAnimalAnnouncement(
                            (params.row as IAnimalAnnouncement).Number,
                        );
                    }}
                    tooltip=''
                    disabled={!canPrint}
                />
            );
        },
    ];

    return (
        <Container id={Strings.AnnouncementListGridContainer}>
            <ADataGrid
                className={'MuiDataGrid-hiddenSortBtns'}
                loading={isLoading}
                actions={dataGridActions}
                columns={getColumns()}
                enablePagination
                enableRowNumbers
                enableFiltering
                rows={props.data
                    .map((i) =>
                        mergeRight(i, {
                            id: i.Id,
                        }),
                    )
                    .sort((a, b) => new Date(b.Created).valueOf() - new Date(a.Created).valueOf())}
                pinnedColumns={{
                    left: [
                        'RowNumber',
                        'Actions',
                        AnimalAnnouncementsListViewStrings.Status,
                        AnimalAnnouncementsListViewStrings.AnnouncementNumberField
                    ]
                }}
            />

            {confirmDialogVisible && (
                <ConfirmDialog
                    data-robot-id='animal-announcementlist-confirm-dialog'
                    title={'AnelmaGeneral:1142'}
                    content={'AnelmaGeneral:1012'}
                    actionButtonType={Strings.AnnouncementDeleteConfirmActionType}
                    onClose={() => setConfirmDialogVisible(false)}
                    onConfirm={() => deleteAnimalAnnouncement()}
                />
            )}

            {chainInformationDialogVisible && (
                <ChainInformationDialog
                    data-robot-id={
                        'animal-announcementlist-chain-information-dialog-' + announcementId
                    }
                    onClose={() => {
                        setChainInformationDialogVisible(false);
                        reloadChainInfo();
                    }}
                    announcementData={null}
                    announcementId={announcementId}
                    announcementNumber={announcementNumber}
                    animals={animalsForChainInformation}
                    forceReadOnly={toggleChainInformationReadOnly(announcementId)}
                    validator={new FormValidator()}
                    medicationNumberFieldValidator={new FormValidator()}
                    companyName={farmForChainInformation?.Name ?? ''}
                    producerNumber={
                        farmForChainInformation?.ProducerNumber
                            ? farmForChainInformation.ProducerNumber.toString()
                            : ''}
                />
            )}
        </Container>
    );
}
